<section class="page">
  <header class="page-header">
    <div>
      <h1>Agencies</h1>
      <p>Real-estate agencies for rent payouts — mobile search catalog + bank/PayID rails</p>
    </div>
    <button class="btn btn-primary" type="button" (click)="openCreate()" [disabled]="saving()">
      Add agency
    </button>
  </header>

  @if (error()) {
    <div class="alert alert-error">{{ error() }}</div>
  }
  @if (success()) {
    <div class="alert alert-success">{{ success() }}</div>
  }

  <div class="filters">
    <input
      type="search"
      [(ngModel)]="search"
      placeholder="Search agencies"
      (keyup.enter)="searchNow()"
    />
    <button class="btn btn-secondary" type="button" (click)="searchNow()" [disabled]="loading()">
      Search
    </button>
  </div>

  @if (showForm()) {
    <div class="panel create-panel">
      <h2>{{ editingId() ? 'Edit agency' : 'Create agency' }}</h2>
      <form [formGroup]="form" (ngSubmit)="save()" class="create-form agency-form">
        <label>
          <span>Name</span>
          <input type="text" formControlName="name" />
        </label>
        <label>
          <span>Slug</span>
          <input type="text" formControlName="slug" placeholder="auto from name if empty" />
        </label>
        <label class="full">
          <span>Subtitle</span>
          <input type="text" formControlName="subtitle" placeholder="e.g. Real estate agency" />
        </label>

        <div class="full image-field">
          <label>
            <span>Image URL (or pick file — uploads to S3 on Save)</span>
            <input
              type="text"
              formControlName="image_url"
              placeholder="https://… or leave empty if uploading file"
            />
          </label>
          <input type="file" accept="image/*" (change)="onFileSelected($event)" />
          @if (pendingImage) {
            <span class="muted">Queued: {{ pendingImage.name }}</span>
          }
          @if (previewUrl()) {
            <img class="preview" [src]="previewUrl()!" alt="Agency preview" />
          }
        </div>

        <label>
          <span>BSB</span>
          <input type="text" formControlName="bsb" placeholder="062000" />
        </label>
        <label>
          <span>Account number</span>
          <input type="text" formControlName="account_number" />
        </label>
        <label>
          <span>Bank name</span>
          <input type="text" formControlName="bank_name" />
        </label>
        <label>
          <span>Currency</span>
          <input type="text" formControlName="currency" />
        </label>

        <label>
          <span>PayID (optional)</span>
          <input type="text" formControlName="pay_id" />
        </label>
        <label>
          <span>PayID type</span>
          <select formControlName="pay_id_type">
            <option value="">—</option>
            <option value="EMAIL">EMAIL</option>
            <option value="PHONE">PHONE</option>
            <option value="ABN">ABN</option>
            <option value="ORG">ORG</option>
          </select>
        </label>
        <label>
          <span>PayID name</span>
          <input type="text" formControlName="pay_id_name" />
        </label>
        <label>
          <span>Sort order</span>
          <input type="number" formControlName="sort_order" />
        </label>

        <label class="checkbox">
          <input type="checkbox" formControlName="is_active" />
          <span>Active</span>
        </label>
        <label class="checkbox">
          <input type="checkbox" formControlName="show_on_mobile" />
          <span>Show on mobile</span>
        </label>

        <p class="full muted hint">
          Create requires bank (account + BSB) or PayID. Saving registers a Kobble beneficiary for rent
          payouts. Mobile never sees bank details.
        </p>

        <div class="actions full">
          <button class="btn btn-primary" type="submit" [disabled]="saving()">
            {{ saving() ? 'Saving…' : 'Save' }}
          </button>
          <button class="btn btn-secondary" type="button" (click)="cancelForm()" [disabled]="saving()">
            Cancel
          </button>
        </div>
      </form>
    </div>
  }

  <div class="panel">
    <div class="table-wrap">
      <table>
        <thead>
          <tr>
            <th></th>
            <th>Agency</th>
            <th>Bank / PayID</th>
            <th>Kobble</th>
            <th>Status</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          @if (loading()) {
            <tr><td colspan="6" class="muted">Loading…</td></tr>
          } @else if (!rows().length) {
            <tr><td colspan="6" class="muted">No agencies yet — add one or run the seed SQL</td></tr>
          } @else {
            @for (row of rows(); track row.id) {
              <tr>
                <td>
                  @if (row.image_url) {
                    <img class="logo" [src]="row.image_url" [alt]="row.name" />
                  } @else {
                    <span class="logo-placeholder">—</span>
                  }
                </td>
                <td>
                  <div class="name">{{ row.name }}</div>
                  <div class="muted">{{ row.subtitle || '—' }}</div>
                  <div class="muted mono">{{ row.slug }}</div>
                </td>
                <td class="bank-cell">{{ bankLabel(row) }}</td>
                <td>
                  @if (row.kobble_beneficiary_id) {
                    <span class="chip ok">linked</span>
                  } @else {
                    <span class="chip warn">pending</span>
                  }
                </td>
                <td>
                  <span
                    class="badge"
                    [attr.data-status]="row.is_active && row.show_on_mobile ? 'active' : 'pending'"
                  >
                    {{ row.is_active ? (row.show_on_mobile ? 'mobile' : 'hidden') : 'inactive' }}
                  </span>
                </td>
                <td class="row-actions">
                  <button class="btn btn-secondary btn-sm" type="button" (click)="openEdit(row)">
                    Edit
                  </button>
                  <button
                    class="btn btn-ghost btn-sm"
                    type="button"
                    (click)="remove(row)"
                    [disabled]="saving()"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>
    @if (total() > limit) {
      <div class="pager">
        <span class="muted">Page {{ page() }} / {{ totalPages() }} ({{ total() }} total)</span>
        <div class="pager-actions">
          <button
            class="btn btn-secondary btn-sm"
            type="button"
            [disabled]="page() <= 1 || loading()"
            (click)="load(page() - 1)"
          >
            Prev
          </button>
          <button
            class="btn btn-secondary btn-sm"
            type="button"
            [disabled]="page() >= totalPages() || loading()"
            (click)="load(page() + 1)"
          >
            Next
          </button>
        </div>
      </div>
    }
  </div>
</section>
